import { useRef } from 'react';

// ── Sticky (hysteresis) display rounding ─────────────────────────────────
//
// Problem: a displayed number rounded from a live sensor value flickers when
// the true value sits near a rounding boundary — 98.50% rounds to 99, a few
// thousandths of noise makes it 98, then 99 again. Averaging would fix it but
// costs responsiveness, and a dead band costs accuracy and lags.
//
// Hysteresis costs neither. We remember the value currently on screen and only
// move to a new one once the raw value has travelled clear of the boundary by
// `margin`. A real change shows immediately (no lag, no averaging); noise
// sitting on the edge can never flip it, because flipping back would require
// travelling the full margin again in the other direction.
//
//   step   = display resolution (1 for whole %, 1/12 ft for whole inches…)
//   margin = step * marginFrac. With the default 0.25 the number changes only
//            after the raw value passes 0.75 of a step, and returning needs
//            another 0.5 — a dead zone wider than any noise we've measured
//            (~0.135% on WT-0001, vs a 0.5% zone here).
export function useSticky(raw, step, marginFrac = 0.25) {
  const held = useRef(null);
  if (raw == null || !Number.isFinite(raw) || !(step > 0)) return null;
  const snapped = Math.round(raw / step) * step;
  if (held.current == null) { held.current = snapped; return snapped; }
  if (Math.abs(raw - held.current) < step / 2 + step * marginFrac) return held.current;
  held.current = snapped;
  return snapped;
}

// A "nice" display step at or below `v` — 1, 2, 5, 10, 25, 50, 100…
// Keeps gallon steps human (25 gal, not 27 gal).
export function niceStep(v) {
  if (!(v > 0)) return 1;
  const steps = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000];
  let best = steps[0];
  for (const s of steps) if (s <= v) best = s;
  return best;
}
